import rawAxios from "axios";
import { axios, APIBASE_REVIVERAPPLICATION, APIBASE_REVIVERUSERS } from "./ApiConfig";
import {
  ReceptionFractionWindow,
  ReceptionHolidayClosure,
  ReceptionOpeningHoursDay,
  ReceptionSlot,
  ReceptionTimeSlotConfig,
} from "@/types";
import { useMutation, UseMutationOptions, useQuery, UseQueryOptions } from "@tanstack/react-query";

const IS_MOCK = !!import.meta.env.VITE_MOCK;

// ── Lazy mock imports ─────────────────────────────────────────────────────────

const mockOpeningHours = () =>
  import("../../tests/mock/receptionOpeningHours.json").then((m) => m.default as unknown as ReceptionOpeningHoursDay[]);
const mockHolidays = () =>
  import("../../tests/mock/receptionHolidays.json").then((m) => m.default as unknown as ReceptionHolidayClosure[]);
const mockSlotConfig = () =>
  import("../../tests/mock/receptionSlotConfig.json").then((m) => m.default as unknown as ReceptionTimeSlotConfig);
const mockFractionWindows = () =>
  import("../../tests/mock/receptionFractionWindows.json").then(
    (m) => m.default as unknown as ReceptionFractionWindow[]
  );
const mockSlots = () =>
  import("../../tests/mock/receptionSlots.json").then((m) => m.default as unknown as ReceptionSlot[]);

// ── Opening Hours — /usr/api/company/{id}/reception/openingHours ──────────────

export const getOpeningHours = (companyId: number) =>
  axios.get<ReceptionOpeningHoursDay[]>(`${APIBASE_REVIVERUSERS}api/company/${companyId}/reception/openingHours`);

export const putOpeningHoursDay = (companyId: number, day: ReceptionOpeningHoursDay) =>
  axios.put<ReceptionOpeningHoursDay>(`${APIBASE_REVIVERUSERS}api/company/${companyId}/reception/openingHours`, day);

export const deleteOpeningHoursDay = (companyId: number, dayOfWeek: number) =>
  axios.delete(`${APIBASE_REVIVERUSERS}api/company/${companyId}/reception/openingHours/${dayOfWeek}`);

export const useGetOpeningHours = (companyId: number, options?: Partial<UseQueryOptions<ReceptionOpeningHoursDay[]>>) =>
  useQuery<ReceptionOpeningHoursDay[]>({
    queryKey: ["receptionOpeningHours", companyId],
    queryFn: async () => {
      if (IS_MOCK) return mockOpeningHours();
      return (await getOpeningHours(companyId)).data;
    },
    refetchOnWindowFocus: false,
    ...options,
  });

export const usePutOpeningHoursDay = (
  companyId: number,
  options?: UseMutationOptions<ReceptionOpeningHoursDay, Error, ReceptionOpeningHoursDay>
) =>
  useMutation<ReceptionOpeningHoursDay, Error, ReceptionOpeningHoursDay>({
    mutationFn: async (day) => {
      if (IS_MOCK) {
        await new Promise((r) => setTimeout(r, 200));
        return day;
      }
      return (await putOpeningHoursDay(companyId, day)).data;
    },
    ...options,
  });

export const useDeleteOpeningHoursDay = (companyId: number, options?: UseMutationOptions<void, Error, number>) =>
  useMutation<void, Error, number>({
    mutationFn: async (dayOfWeek) => {
      if (IS_MOCK) {
        await new Promise((r) => setTimeout(r, 200));
        return;
      }
      await deleteOpeningHoursDay(companyId, dayOfWeek);
    },
    ...options,
  });

// ── Holidays — /usr/api/company/{id}/reception/holidays ──────────────────────

export const getHolidays = (companyId: number) =>
  axios.get<ReceptionHolidayClosure[]>(`${APIBASE_REVIVERUSERS}api/company/${companyId}/reception/holidays`);

export const putHoliday = (companyId: number, holiday: ReceptionHolidayClosure) =>
  axios.put<ReceptionHolidayClosure>(`${APIBASE_REVIVERUSERS}api/company/${companyId}/reception/holidays`, holiday);

export const deleteHoliday = (companyId: number, date: string) =>
  axios.delete(`${APIBASE_REVIVERUSERS}api/company/${companyId}/reception/holidays/${date}`);

export const useGetHolidays = (companyId: number, options?: Partial<UseQueryOptions<ReceptionHolidayClosure[]>>) =>
  useQuery<ReceptionHolidayClosure[]>({
    queryKey: ["receptionHolidays", companyId],
    queryFn: async () => {
      if (IS_MOCK) return mockHolidays();
      return (await getHolidays(companyId)).data;
    },
    refetchOnWindowFocus: false,
    ...options,
  });

export const usePutHoliday = (
  companyId: number,
  options?: UseMutationOptions<ReceptionHolidayClosure, Error, ReceptionHolidayClosure>
) =>
  useMutation<ReceptionHolidayClosure, Error, ReceptionHolidayClosure>({
    mutationFn: async (holiday) => {
      if (IS_MOCK) {
        await new Promise((r) => setTimeout(r, 200));
        return holiday;
      }
      return (await putHoliday(companyId, holiday)).data;
    },
    ...options,
  });

export const useDeleteHoliday = (companyId: number, options?: UseMutationOptions<void, Error, string>) =>
  useMutation<void, Error, string>({
    mutationFn: async (date) => {
      if (IS_MOCK) {
        await new Promise((r) => setTimeout(r, 200));
        return;
      }
      await deleteHoliday(companyId, date);
    },
    ...options,
  });

// ── Slot Config — /usr/api/company/{id}/reception/slotConfig ─────────────────

export const getSlotConfig = (companyId: number) =>
  axios.get<ReceptionTimeSlotConfig>(`${APIBASE_REVIVERUSERS}api/company/${companyId}/reception/slotConfig`);

export const putSlotConfig = (companyId: number, config: ReceptionTimeSlotConfig) =>
  axios.put<ReceptionTimeSlotConfig>(`${APIBASE_REVIVERUSERS}api/company/${companyId}/reception/slotConfig`, config);

// A 404 here is a documented, expected response meaning "no slot configuration exists yet" —
// not an error state. It resolves to null so the UI can render a "not configured" empty state
// instead of getting stuck in React Query's error state.
export const useGetSlotConfig = (
  companyId: number,
  options?: Partial<UseQueryOptions<ReceptionTimeSlotConfig | null>>
) =>
  useQuery<ReceptionTimeSlotConfig | null>({
    queryKey: ["receptionSlotConfig", companyId],
    queryFn: async () => {
      if (IS_MOCK) return mockSlotConfig();
      try {
        return (await getSlotConfig(companyId)).data;
      } catch (error) {
        if (rawAxios.isAxiosError(error) && error.response?.status === 404) return null;
        throw error;
      }
    },
    refetchOnWindowFocus: false,
    ...options,
  });

export const usePutSlotConfig = (
  companyId: number,
  options?: UseMutationOptions<ReceptionTimeSlotConfig, Error, ReceptionTimeSlotConfig>
) =>
  useMutation<ReceptionTimeSlotConfig, Error, ReceptionTimeSlotConfig>({
    mutationFn: async (config) => {
      if (IS_MOCK) {
        await new Promise((r) => setTimeout(r, 300));
        return config;
      }
      return (await putSlotConfig(companyId, config)).data;
    },
    ...options,
  });

// ── Fraction Windows — /usr/api/company/{id}/reception/fractionWindows ────────

export const getFractionWindows = (companyId: number) =>
  axios.get<ReceptionFractionWindow[]>(`${APIBASE_REVIVERUSERS}api/company/${companyId}/reception/fractionWindows`);

export const postFractionWindow = (companyId: number, window: Omit<ReceptionFractionWindow, "id">) =>
  axios.post<ReceptionFractionWindow>(
    `${APIBASE_REVIVERUSERS}api/company/${companyId}/reception/fractionWindows`,
    window
  );

export const putFractionWindow = (companyId: number, window: ReceptionFractionWindow) =>
  axios.put<ReceptionFractionWindow>(
    `${APIBASE_REVIVERUSERS}api/company/${companyId}/reception/fractionWindows/${window.id}`,
    window
  );

export const deleteFractionWindow = (companyId: number, windowId: number) =>
  axios.delete(`${APIBASE_REVIVERUSERS}api/company/${companyId}/reception/fractionWindows/${windowId}`);

export const useGetFractionWindows = (
  companyId: number,
  options?: Partial<UseQueryOptions<ReceptionFractionWindow[]>>
) =>
  useQuery<ReceptionFractionWindow[]>({
    queryKey: ["receptionFractionWindows", companyId],
    queryFn: async () => {
      if (IS_MOCK) return mockFractionWindows();
      return (await getFractionWindows(companyId)).data;
    },
    refetchOnWindowFocus: false,
    ...options,
  });

export const useUpsertFractionWindow = (
  companyId: number,
  options?: UseMutationOptions<
    ReceptionFractionWindow,
    Error,
    ReceptionFractionWindow | Omit<ReceptionFractionWindow, "id">
  >
) =>
  useMutation<ReceptionFractionWindow, Error, ReceptionFractionWindow | Omit<ReceptionFractionWindow, "id">>({
    mutationFn: async (window) => {
      if (IS_MOCK) {
        await new Promise((r) => setTimeout(r, 300));
        return { id: Date.now(), ...window } as ReceptionFractionWindow;
      }
      if ("id" in window && window.id != null) {
        return (await putFractionWindow(companyId, window as ReceptionFractionWindow)).data;
      }
      return (await postFractionWindow(companyId, window)).data;
    },
    ...options,
  });

export const useDeleteFractionWindow = (companyId: number, options?: UseMutationOptions<void, Error, number>) =>
  useMutation<void, Error, number>({
    mutationFn: async (windowId) => {
      if (IS_MOCK) {
        await new Promise((r) => setTimeout(r, 200));
        return;
      }
      await deleteFractionWindow(companyId, windowId);
    },
    ...options,
  });

// ── Slot Generation — POST /app/api/reception/{id}/slots/generate ─────────────

export const postGenerateSlots = (receiverCompanyId: number, fromDate: string, toDate: string) =>
  axios.post(`${APIBASE_REVIVERAPPLICATION}api/reception/${receiverCompanyId}/slots/generate`, null, {
    params: { fromDate, toDate },
  });

export const useGenerateSlots = (
  receiverCompanyId: number,
  options?: UseMutationOptions<void, Error, { fromDate: string; toDate: string }>
) =>
  useMutation<void, Error, { fromDate: string; toDate: string }>({
    mutationFn: async ({ fromDate, toDate }) => {
      if (IS_MOCK) {
        await new Promise((r) => setTimeout(r, 800));
        return;
      }
      await postGenerateSlots(receiverCompanyId, fromDate, toDate);
    },
    ...options,
  });

// ── Available Slots — GET /app/api/reception/{id}/slots ──────────────────────

export const getReceptionSlots = (
  receiverCompanyId: number,
  fromDate: string,
  toDate: string,
  fractionId?: number | null
) =>
  axios.get<ReceptionSlot[]>(`${APIBASE_REVIVERAPPLICATION}api/reception/${receiverCompanyId}/slots`, {
    params: { fromDate, toDate, ...(fractionId != null ? { fractionId } : {}) },
  });

export const useGetReceptionSlots = (
  receiverCompanyId: number,
  fromDate: string,
  toDate: string,
  fractionId?: number | null,
  options?: Partial<UseQueryOptions<ReceptionSlot[]>>
) =>
  useQuery<ReceptionSlot[]>({
    queryKey: ["receptionSlots", receiverCompanyId, fromDate, toDate, fractionId ?? null],
    queryFn: async () => {
      if (IS_MOCK) return mockSlots();
      return (await getReceptionSlots(receiverCompanyId, fromDate, toDate, fractionId)).data;
    },
    refetchOnWindowFocus: false,
    ...options,
  });

// ── Calendar Slots — GET /app/api/reception/{id}/slots/calendar ──────────────
// Unlike /slots, this includes full and past slots — for display only, never for
// reservation selection (reservation must still go through /slots + backend revalidation).

export const getReceptionCalendarSlots = (
  receiverCompanyId: number,
  fromDate: string,
  toDate: string,
  fractionId?: number | null
) =>
  axios.get<ReceptionSlot[]>(`${APIBASE_REVIVERAPPLICATION}api/reception/${receiverCompanyId}/slots/calendar`, {
    params: { fromDate, toDate, ...(fractionId != null ? { fractionId } : {}) },
  });

export const useGetReceptionCalendarSlots = (
  receiverCompanyId: number,
  fromDate: string,
  toDate: string,
  fractionId?: number | null,
  options?: Partial<UseQueryOptions<ReceptionSlot[]>>
) =>
  useQuery<ReceptionSlot[]>({
    queryKey: ["receptionCalendarSlots", receiverCompanyId, fromDate, toDate, fractionId ?? null],
    queryFn: async () => {
      if (IS_MOCK) return mockSlots();
      return (await getReceptionCalendarSlots(receiverCompanyId, fromDate, toDate, fractionId)).data;
    },
    refetchOnWindowFocus: false,
    ...options,
  });
